"use client";

import { useMemo, useState } from "react";
import { Globe, Search } from "lucide-react";
import { useCustomerMarketplace as useMarketplace } from "@/features/shop/customer/customer-shop.queries";
import { PublicStoreCard } from "@/features/public-site/components/PublicStoreCard";
import { LoadingSkeleton } from "@/components/loading-skeleton";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function StoreListPage() {
  const [searchValue, setSearchValue] = useState("");
  const [countyFilter, setCountyFilter] = useState("");

  const marketplace = useMarketplace({
    locationSearch: searchValue.trim() || undefined,
    county: countyFilter.trim() || undefined,
  });

  const shops = useMemo(
    () => marketplace.data?.data?.shops ?? [],
    [marketplace.data?.data?.shops],
  );
  const products = useMemo(
    () => marketplace.data?.data?.products ?? [],
    [marketplace.data?.data?.products],
  );

  const counties = useMemo(
    () =>
      Array.from(
        new Set(shops.map((shop) => shop.county).filter(Boolean)),
      ) as string[],
    [shops],
  );

  const totalLabel = `${shops.length} Store${shops.length === 1 ? "" : "s"} Found`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-7xl px-4 py-4 grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <section className="animate-in fade-in slide-in-from-left-4 rounded bg-card p-4 shadow-sm duration-500 fill-mode-both md:p-6">
          <div className="py-1 text-sm font-semibold ">{totalLabel}</div>
          <div className="my-3">
            <label
              htmlFor="search"
              className="block text-sm font-semibold text-foreground/80 mb-1"
            >
              Search Stores
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search by store name, city, or address..."
                className="h-12 rounded border-emerald-100 bg-muted pl-11 shadow-sm"
              />
            </div>
          </div>
          <div className="">
            <label
              htmlFor="county"
              className="block text-sm font-semibold text-foreground/80 mb-1"
            >
              Filter by County
            </label>
            <div className="relative block">
              <Globe className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
              <Select
                value={countyFilter || "__all__"}
                onValueChange={(value) => setCountyFilter(value === "__all__" ? "" : value)}
              >
                <SelectTrigger
                  id="county"
                  className="h-12 w-full min-w-[220px] rounded border-emerald-100 bg-muted pl-11 text-sm text-foreground/80 shadow-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Counties</SelectItem>
                  {counties.map((county) => (
                    <SelectItem key={county} value={county}>
                      {county}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 fill-mode-both sm:col-span-2 lg:col-span-3">
          <div className="space-y-4">
            {marketplace.isLoading ? (
              <LoadingSkeleton
                variant="cards"
                count={6}
                gridClassName="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
              />
            ) : marketplace.error ? (
              <div className="rounded border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
                {marketplace.error.message}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {shops.map((shop) => {
                  const productCount = products.filter((product) => {
                    if (typeof product.shopId === "string")
                      return product.shopId === shop._id;
                    return product.shopId?._id === shop._id;
                  }).length;

                  return (
                    <PublicStoreCard
                      key={shop._id}
                      shop={shop}
                      productCount={productCount}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
